import { useEffect, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { EVENT_TYPE_LABEL, REPLACEMENT_KIND_LABEL, type AssetEventView } from "../api/assets";
import type { Page } from "../api/admin";
import type { ApiError } from "../api/client";
import { ConditionChip } from "./ConditionChip";
import { Pagination } from "./Pagination";
import { formatDateTime } from "../lib/dates";
import { shortPathLabel } from "../lib/paths";

type Translate = (key: string, fallback: string) => string;

export function eventTypeLabel(t: Translate, type: AssetEventView["eventType"]): string {
  return t(`assets.event.${type}`, EVENT_TYPE_LABEL[type] ?? type);
}

/**
 * Paginated event history (asset karton or position history). `load` is the
 * page fetcher; bump `refreshKey` after a write to reload the first page.
 */
export function AssetEventsTable({
  load, pageSize = 20, refreshKey = 0, showAsset = false, emptyText,
}: {
  load: (page: number, size: number) => Promise<Page<AssetEventView>>;
  pageSize?: number;
  refreshKey?: number;
  /** Adds an asset column (position history lists several assets). */
  showAsset?: boolean;
  emptyText?: string;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [data, setData] = useState<Page<AssetEventView> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setPage(0); }, [refreshKey, load]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    load(page, pageSize)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err: ApiError) => { if (!cancelled) { setError(err.message); setData({ content: [], totalElements: 0, totalPages: 0, number: 0, size: pageSize }); } });
    return () => { cancelled = true; };
  }, [load, page, pageSize, refreshKey]);

  if (error) return <div style={errorBox} role="alert">{error}</div>;
  if (data === null) return <div className="empty">{t("common.loading", "Učitavanje…")}</div>;
  if (data.content.length === 0) return <div className="empty">{emptyText ?? t("assets.events.empty", "Još nema događaja.")}</div>;

  return (
    <>
      <div className="tbl-wrap">
        <table className="tbl list-card events-tbl">
          <thead>
            <tr>
              <th>{t("assets.events.col.when", "Kada")}</th>
              <th>{t("assets.events.col.what", "Šta")}</th>
              {showAsset && <th>{t("assets.events.col.asset", "Asset")}</th>}
              {/* On the position's own history these two are secondary — tablets drop them (styles.css). */}
              <th className={showAsset ? "hide-tablet" : undefined}>{t("assets.events.col.who", "Ko")}</th>
              <th className={showAsset ? "hide-tablet" : undefined}>{t("assets.events.col.position", "Pozicija")}</th>
              <th>{t("assets.events.col.fromTo", "Odakle → Kuda")}</th>
              <th>{t("assets.events.col.condition", "Stanje")}</th>
              <th>{t("assets.events.col.note", "Napomena")}</th>
            </tr>
          </thead>
          <tbody>
            {data.content.map((ev) => <EventRow key={ev.id} ev={ev} showAsset={showAsset} />)}
          </tbody>
        </table>
      </div>
      <Pagination page={page} totalPages={data.totalPages} onChange={setPage} />
    </>
  );
}

function EventRow({ ev, showAsset }: { ev: AssetEventView; showAsset: boolean }) {
  const { t } = useTranslation();
  const pos = ev.positionId ? shortPathLabel(ev.positionPath, ev.positionName) : null;
  const posLink = pos && ev.positionId
    ? <Link to={`/struktura/${ev.positionId}`} title={pos.full}>{pos.short}</Link>
    : null;

  // "from → to": MOUNT goes location → position, DISMOUNT position → location, MOVE location → location.
  let from: React.ReactNode = ev.fromLocation ? locLabel(ev.fromLocation) : null;
  let to: React.ReactNode = ev.toLocation ? locLabel(ev.toLocation) : null;
  if (ev.eventType === "MOUNT") to = posLink ?? to;
  if (ev.eventType === "DISMOUNT") from = posLink ?? from;
  const showArrow = ev.eventType === "MOUNT" || ev.eventType === "DISMOUNT" || ev.eventType === "MOVE";

  return (
    <tr>
      <td data-label={t("assets.events.col.when", "Kada")} className="mono" style={{ whiteSpace: "nowrap" }}>{formatDateTime(ev.occurredAt)}</td>
      <td data-label={t("assets.events.col.what", "Šta")}>
        <b>{eventTypeLabel(t, ev.eventType)}</b>
        {ev.eventType === "MOUNT" && ev.replacementKind && (
          <span className={`tag ${ev.replacementKind === "TEMPORARY" ? "warn" : ""}`} style={{ marginLeft: 6 }}>
            {t(`assets.replacementKind.${ev.replacementKind}`, REPLACEMENT_KIND_LABEL[ev.replacementKind])}
          </span>
        )}
      </td>
      {showAsset && (
        <td data-label={t("assets.events.col.asset", "Asset")}>
          <Link to={`/asseti/${ev.assetId}`} className="mono">{ev.assetTag}</Link>
          <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}>{ev.assetTypeName}</span>
        </td>
      )}
      <td className={showAsset ? "hide-tablet" : undefined} data-label={t("assets.events.col.who", "Ko")}>{ev.performedByName}</td>
      <td className={showAsset ? "hide-tablet" : undefined} data-label={t("assets.events.col.position", "Pozicija")}>{posLink ?? <span className="muted">—</span>}</td>
      <td data-label={t("assets.events.col.fromTo", "Odakle → Kuda")}>
        {showArrow ? (
          <span className="from-to">
            <span>{from ?? <span className="muted">—</span>}</span>
            <span className="arrow" aria-hidden="true">→</span>
            <span>{to ?? <span className="muted">—</span>}</span>
          </span>
        ) : <span className="muted">—</span>}
      </td>
      <td data-label={t("assets.events.col.condition", "Stanje")}>
        {ev.conditionAfter ? (
          <span className="from-to">
            {ev.conditionBefore && ev.conditionBefore !== ev.conditionAfter && (
              <>
                <ConditionChip condition={ev.conditionBefore} />
                <span className="arrow" aria-hidden="true">→</span>
              </>
            )}
            <ConditionChip condition={ev.conditionAfter} />
          </span>
        ) : <span className="muted">—</span>}
      </td>
      <td data-label={t("assets.events.col.note", "Napomena")} style={{ whiteSpace: "pre-wrap", maxWidth: 320 }}>{ev.note || <span className="muted">—</span>}</td>
    </tr>
  );
}

function locLabel(loc: { name: string; code: string }) {
  return <span title={loc.code}>{loc.name}</span>;
}

const errorBox: CSSProperties = { padding: 10, margin: 18, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
